import React, { useState, useRef, useMemo } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Download, Copy, QrCode, Smartphone, FileText } from 'lucide-react';

const STORAGE_KEY = 'playcity-onboarding-url';
const DEFAULT_URL = 'https://www.playcity.ci';

// Copie texte (robuste iframe)
const copyTextToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
};

const AdminOnboarding = () => {
  const [url, setUrl] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_URL;
    } catch (_) {
      return DEFAULT_URL;
    }
  });
  const qrWrapperRef = useRef(null);

  const safeUrl = useMemo(() => {
    const trimmed = (url || '').trim();
    if (!trimmed) return DEFAULT_URL;
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }, [url]);

  const persistUrl = (val) => {
    setUrl(val);
    try {
      localStorage.setItem(STORAGE_KEY, val);
    } catch (_) {}
  };

  // Génère un PNG haute résolution (1024x1274) à partir du canvas QR
  const generatePngBlob = () =>
    new Promise((resolve, reject) => {
      const canvas = qrWrapperRef.current?.querySelector('canvas');
      if (!canvas) {
        reject(new Error('Canvas QR introuvable'));
        return;
      }
      const SIZE = 1024;
      const PADDING = 64;
      const HEADER = 140;
      const FOOTER = 110;
      const out = document.createElement('canvas');
      out.width = SIZE;
      out.height = SIZE + HEADER + FOOTER;
      const ctx = out.getContext('2d');

      ctx.fillStyle = '#0B132B';
      ctx.fillRect(0, 0, out.width, out.height);

      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 64px -apple-system, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('PlayCity', out.width / 2, 80);
      ctx.fillStyle = '#C6A15B';
      ctx.font = '500 28px -apple-system, system-ui, sans-serif';
      ctx.fillText('Scannez pour installer l’app', out.width / 2, 120);

      ctx.fillStyle = '#FFFFFF';
      const qrX = (out.width - SIZE + PADDING * 2) / 2;
      const qrY = HEADER;
      ctx.fillRect(qrX, qrY, SIZE - PADDING * 2, SIZE - PADDING * 2);
      ctx.drawImage(canvas, qrX + 16, qrY + 16, SIZE - PADDING * 2 - 32, SIZE - PADDING * 2 - 32);

      ctx.fillStyle = '#EAEAEA';
      ctx.font = '500 24px -apple-system, system-ui, sans-serif';
      ctx.fillText(safeUrl, out.width / 2, SIZE + HEADER + 50);
      ctx.fillStyle = '#C6A15B';
      ctx.font = '400 20px -apple-system, system-ui, sans-serif';
      ctx.fillText('iPhone & Android compatible', out.width / 2, SIZE + HEADER + 85);

      out.toBlob((blob) => {
        if (!blob || blob.size === 0) reject(new Error('Blob vide'));
        else resolve(blob);
      }, 'image/png');
    });

  // Bouton 1 : télécharger PNG (toujours fiable)
  const downloadQrPng = async () => {
    try {
      const blob = await generatePngBlob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'playcity-qr.png';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      toast.success(`QR téléchargé (playcity-qr.png — ${Math.round(blob.size / 1024)} Ko)`);
    } catch (e) {
      toast.error('Erreur génération QR : ' + (e?.message || 'inconnu'));
    }
  };

  // Bouton 2 : copier QR (image) — UNIQUEMENT si Clipboard API supporte image/png. Sinon message honnête.
  const copyQrImageToClipboard = async () => {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      toast.error(
        'Copie image non supportée ici. Utilisez « Télécharger QR PNG » puis glissez le fichier dans WhatsApp.',
        { duration: 7000 }
      );
      return;
    }
    try {
      const blob = await generatePngBlob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': Promise.resolve(blob) }),
      ]);
      toast.success('QR image copié — ouvrez WhatsApp puis Ctrl+V (Cmd+V)');
    } catch (_) {
      toast.error(
        'Copie image refusée par le navigateur. Utilisez « Télécharger QR PNG » puis glissez le fichier dans WhatsApp.',
        { duration: 7000 }
      );
    }
  };

  // Bouton 3 : copier message + lien (texte uniquement)
  const copyMessageWithLink = async () => {
    const message = `Bonjour ! Voici l'application PlayCity à installer sur votre téléphone (iPhone ou Android) :\n\n${safeUrl}\n\nOuvrez le lien depuis votre téléphone, le pop-up "Installer PlayCity" apparaîtra automatiquement.`;
    const ok = await copyTextToClipboard(message);
    if (ok) toast.success('Message + lien copiés — collez-les dans WhatsApp');
    else toast.error('Copie texte bloquée — sélectionnez l’URL dans le champ ci-dessus');
  };

  return (
    <Card className="bg-[#1C2541] border-[#C6A15B]/30">
      <CardHeader>
        <CardTitle className="text-[#FFD700] flex items-center gap-2">
          <QrCode className="w-5 h-5" />
          Onboarding équipe — QR Code installation
        </CardTitle>
        <p className="text-[#EAEAEA]/60 text-sm mt-1">
          Téléchargez le QR puis envoyez-le à vos commerciaux par WhatsApp. Ils scannent → l’app PlayCity s’installe sur leur écran d’accueil (iPhone ou Android).
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* URL configurable */}
        <div>
          <Label htmlFor="onboarding-url" className="text-[#EAEAEA]">
            URL publique (production ou dev)
          </Label>
          <div className="flex gap-2 mt-1">
            <Input
              id="onboarding-url"
              data-testid="onboarding-url-input"
              value={url}
              onChange={(e) => persistUrl(e.target.value)}
              placeholder="https://www.playcity.ci"
              className="bg-[#0B132B] border-[#C6A15B]/40 text-[#EAEAEA]"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => persistUrl(DEFAULT_URL)}
              data-testid="onboarding-url-reset-btn"
              className="border-[#C6A15B]/40 text-[#EAEAEA] hover:bg-[#C6A15B]/10"
            >
              Défaut
            </Button>
          </div>
        </div>

        {/* Visualisation QR + actions */}
        <div className="flex flex-col md:flex-row items-center gap-6 bg-[#0B132B] border border-[#C6A15B]/20 rounded-2xl p-6">
          <div
            ref={qrWrapperRef}
            data-testid="onboarding-qr-wrapper"
            className="bg-white rounded-2xl p-4 shadow-xl shrink-0"
          >
            <QRCodeCanvas
              value={safeUrl}
              size={240}
              level="H"
              includeMargin={false}
              fgColor="#0B132B"
              bgColor="#FFFFFF"
            />
          </div>

          <div className="flex-1 w-full space-y-3">
            <div className="flex items-center gap-2 text-[#FFD700] font-bold">
              <Smartphone className="w-5 h-5" />
              <span>iPhone & Android compatibles</span>
            </div>
            <div className="text-[#EAEAEA]/75 text-sm leading-relaxed">
              Le commercial scanne le QR avec l’appareil photo de son téléphone. La page PlayCity s’ouvre, et un pop-up <strong className="text-[#FFD700]">« Installer PlayCity »</strong> apparaît automatiquement.
            </div>

            {/* Bouton principal : Télécharger QR PNG (toujours fiable) */}
            <Button
              type="button"
              onClick={downloadQrPng}
              data-testid="onboarding-download-qr-btn"
              className="w-full bg-gradient-to-r from-[#FFD700] to-[#C6A15B] hover:from-[#FFE040] hover:to-[#D4B06B] text-[#0B132B] font-bold text-base py-5"
            >
              <Download className="w-5 h-5 mr-2" />
              Télécharger QR PNG
            </Button>

            {/* Boutons secondaires */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={copyQrImageToClipboard}
                data-testid="onboarding-copy-qr-btn"
                variant="outline"
                className="border-[#C6A15B]/40 text-[#EAEAEA] hover:bg-[#C6A15B]/10"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier QR image
              </Button>
              <Button
                type="button"
                onClick={copyMessageWithLink}
                data-testid="onboarding-copy-msg-btn"
                variant="outline"
                className="border-[#C6A15B]/40 text-[#EAEAEA] hover:bg-[#C6A15B]/10"
              >
                <FileText className="w-4 h-4 mr-2" />
                Copier message + lien
              </Button>
            </div>
          </div>
        </div>

        {/* Aide */}
        <div className="bg-[#0B132B]/60 border border-[#C6A15B]/15 rounded-xl p-4 text-xs text-[#EAEAEA]/70 leading-relaxed space-y-1.5">
          <div>
            <strong className="text-[#FFD700]">Méthode recommandée :</strong> cliquez « Télécharger QR PNG » puis <strong>glissez</strong> le fichier <code className="text-[#C6A15B]">playcity-qr.png</code> dans la conversation WhatsApp Desktop de votre équipe.
          </div>
          <div>
            <strong className="text-[#FFD700]">Alternative :</strong> « Copier QR image » fonctionne en production sur Chrome/Edge/Safari récents (faites Ctrl+V dans WhatsApp). Si votre navigateur bloque la copie image, utilisez la méthode recommandée.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminOnboarding;
